import { useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  AlertDialog,
  Textarea,
} from "@/design/components";
import { demoModeActive } from "@/domain/api";
import type { ProjectEditRequest } from "@/domain/api";
import {
  useProjectEditRequests,
  useApproveProjectEditRequest,
  useRejectProjectEditRequest,
} from "@/domain/useProjectEditRequest";

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  description: "Description",
  region: "Region",
  district: "District",
  budgeted: "Budget",
};

function formatValue(v: unknown): string {
  if (v == null || v === "") return "—";
  return String(v);
}

// ZEUS-130/144 — the stratus_admin review surface for pending contractor
// edit requests, embedded in ProjectManagementView's audit tab (per the
// confirmed decision: edit requests are already project-scoped, so review
// lives on the project's own page rather than a global cross-project
// queue like project-creation-request got). Real-API only — see
// useProjectEditRequest.ts's file-level note; renders nothing in demo mode.
export function PendingEditRequestPanel({ projectId }: { projectId: string }) {
  const [rejectTarget, setRejectTarget] = useState<ProjectEditRequest | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<ProjectEditRequest | null>(null);

  const { data: requests } = useProjectEditRequests(projectId);
  const { approveProjectEditRequest, isPending: approving } = useApproveProjectEditRequest();

  if (demoModeActive()) return null;

  const pending = requests ?? [];
  if (pending.length === 0) return null;

  async function approve(request: ProjectEditRequest) {
    const result = await approveProjectEditRequest(request.id, projectId);
    if (result) toast.success("Edit approved and applied.");
    else toast.error("Couldn't approve the edit — please retry");
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">Pending edit request{pending.length > 1 ? "s" : ""}</h3>
        <span className="text-xs text-faint">{pending.length} awaiting review</span>
      </div>
      <ul className="space-y-3">
        {pending.map((request) => (
          <li key={request.id} className="rounded-lg border border-line bg-card p-4">
            <p className="text-sm text-mute">{request.justification}</p>
            <ul className="mt-3 divide-y divide-hairline rounded-md border border-line">
              {Object.entries(request.changediff).map(([field, diff]) => (
                <li key={field} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="font-medium text-ink">{FIELD_LABELS[field] ?? field}</span>
                  <span className="flex items-center gap-2 text-mute">
                    <span className="line-through">{formatValue(diff.from)}</span>
                    <span className="text-faint">→</span>
                    <span className="text-ink">{formatValue(diff.to)}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setRejectTarget(request)}>Reject</Button>
              <Button size="sm" disabled={approving} onClick={() => setConfirmApprove(request)}>Approve</Button>
            </div>
          </li>
        ))}
      </ul>

      <AlertDialog
        open={confirmApprove != null}
        onOpenChange={(o) => !o && setConfirmApprove(null)}
        title="Approve this edit?"
        description={
          confirmApprove
            ? `This applies the proposed changes to "${confirmApprove.previousstate?.name ?? "this project"}" and returns it to Published.`
            : undefined
        }
        confirmLabel="Approve"
        onConfirm={() => { const r = confirmApprove; if (r) approve(r); }}
      />
      <RejectEditRequestDrawer
        request={rejectTarget}
        projectId={projectId}
        onClose={() => setRejectTarget(null)}
      />
    </div>
  );
}

// Reject a pending edit request with a required reason — mirrors
// RejectGateDrawer/RejectProjectRequestDrawer in ApprovalQueueTab.tsx.
function RejectEditRequestDrawer({
  request,
  projectId,
  onClose,
}: {
  request: ProjectEditRequest | null;
  projectId: string;
  onClose: () => void;
}) {
  const { rejectProjectEditRequest, isPending } = useRejectProjectEditRequest();
  const [reason, setReason] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (!request) return null;

  async function send() {
    if (!reason.trim() || !request) return;
    const ok = await rejectProjectEditRequest(request.id, reason.trim(), projectId);
    if (ok) {
      toast.message("Edit request rejected — fields restored.");
      setReason("");
      onClose();
    } else {
      toast.error("Couldn't reject the request — please retry");
    }
  }
  function requestClose(next: boolean) {
    if (next) return;
    if (reason.trim()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  return (
    <Drawer open onOpenChange={requestClose} width="520px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">Reject this edit request</DrawerTitle>
        <p className="mt-1 text-xs text-faint">
          The project's fields are restored to their prior values. The submitter is notified with
          your reason, and it's recorded in the audit trail.
        </p>
      </DrawerHeader>
      <DrawerBody>
        <Textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for rejection — sent to the submitter and recorded (required)"
          className="h-28"
        />
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={() => requestClose(false)}>Cancel</Button>
        <Button onClick={send} disabled={!reason.trim() || isPending}>Reject request</Button>
      </DrawerFooter>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard this rejection?"
        description="Your reason has not been submitted. If you close now it will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => { setReason(""); onClose(); }}
      />
    </Drawer>
  );
}
